// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useVirtualizer } from "@tanstack/react-virtual";
import { CircleCheck, CircleX, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

const ROW_HEIGHT = 40;
const GRID_BASE = "grid items-center gap-2 px-2";

/** One row's status for the preview: valid to create, already exists, or has validation errors. */
export interface PreviewRowMeta {
  index: number;
  valid: boolean;
  exists: boolean;
  errors: string[];
}

/** One preview column: the import field it resolves, and its (already translated) header. */
export interface PreviewColumn<K extends string> {
  key: K;
  label: string;
}

interface ImportPreviewTableProps<K extends string> {
  rows: PreviewRowMeta[];
  /** Columns to show, in order (e.g. core fields plus any enabled optional section). */
  fields: PreviewColumn<K>[];
  /** Resolve a cell value for a given original row index + field. */
  getCell: (rowIndex: number, field: K) => string;
  /** Selectable mode: render a checkbox column, surface "already exists", and virtualize the list. */
  selectable?: boolean;
  selected?: Set<number>;
  onToggle?: (index: number) => void;
  onToggleAll?: () => void;
  allSelected?: boolean;
  /** Read-only mode: render only the first N rows inline (no scroll/virtualization). */
  maxRows?: number;
}

/**
 * Shared preview of resolved spreadsheet-import rows (bulk store / bulk user create). Read-only (map step, first `maxRows`) and selectable
 * (preview step, virtualized with checkboxes) render through the same content-fit grid, so both
 * steps look identical. Columns size to their longest value (no truncation); wide content and long
 * lists scroll horizontally/vertically inside the bordered box. "Already exists" is surfaced only in
 * the selectable step.
 */
export function ImportPreviewTable<K extends string>({
  rows,
  fields,
  getCell,
  selectable,
  selected,
  onToggle,
  onToggleAll,
  allSelected,
  maxRows,
}: ImportPreviewTableProps<K>) {
  const { t } = useTranslation("back-office");
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  const showExists = !!selectable; // "already exists" is only meaningful when picking what to create

  // Content-fit column widths (px): longest cell/header per field, clamped. Shared by the header and
  // every row so they stay aligned; content wider than the (fixed-width) dialog scrolls horizontally.
  const columnPx = useMemo(() => {
    const sample = maxRows ? rows.slice(0, maxRows) : rows;
    return fields.map((field) => {
      let longest = field.label.length;
      for (const meta of sample) longest = Math.max(longest, getCell(meta.index, field.key).length);
      return Math.min(Math.max(Math.round(longest * 7.5) + 24, 88), 480);
    });
  }, [rows, fields, getCell, maxRows]);

  const gridTemplateColumns = [selectable ? "2.25rem" : null, "2.25rem", ...columnPx.map((w) => `${w}px`)]
    .filter(Boolean)
    .join(" ");

  const statusIcon = (meta: PreviewRowMeta) => {
    if (!meta.valid) return <CircleX className="h-4 w-4 text-destructive" />;
    if (showExists && meta.exists) return <TriangleAlert className="h-4 w-4 text-amber-500" />;
    return <CircleCheck className="h-4 w-4 text-primary" />;
  };
  const rowTint = (meta: PreviewRowMeta) =>
    !meta.valid ? "bg-destructive/5" : showExists && meta.exists ? "bg-amber-50 dark:bg-amber-950/30" : undefined;

  const cells = (meta: PreviewRowMeta) => (
    <>
      <span className="flex items-center justify-center" title={meta.errors.join("; ")}>
        {statusIcon(meta)}
      </span>
      {fields.map((field) => (
        <span key={field.key} className="whitespace-nowrap">
          {getCell(meta.index, field.key) || <span className="text-muted-foreground">—</span>}
        </span>
      ))}
    </>
  );

  return (
    // Single scroll container (both axes) so the vertical + horizontal scrollbars sit on the visible
    // viewport, not off at the far edge of the wide content. Selectable mode also scrolls vertically.
    <div
      ref={selectable ? setScrollEl : undefined}
      className={cn("rounded-md border", selectable ? "max-h-[46vh] overflow-auto" : "overflow-x-auto")}
    >
      {/* Fill the box when narrow, grow to content (and scroll as one) when wide. */}
      <div className="w-max min-w-full">
        <div
          className={cn(GRID_BASE, "sticky top-0 z-10 h-10 border-b bg-sidebar text-sm font-medium text-foreground")}
          style={{ gridTemplateColumns }}
        >
          {selectable && (
            <span className="flex items-center justify-center">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={allSelected}
                onChange={onToggleAll}
                aria-label={t("common.selectAll")}
              />
            </span>
          )}
          <span />
          {fields.map((field) => (
            <span key={field.key} className="whitespace-nowrap">
              {field.label}
            </span>
          ))}
        </div>

        {selectable ? (
          <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
            {virtualizer.getVirtualItems().map((vItem) => {
              const meta = rows[vItem.index];
              if (!meta) return null;
              return (
                <div
                  key={meta.index}
                  className={cn(
                    GRID_BASE,
                    "absolute left-0 right-0 text-sm",
                    vItem.index < rows.length - 1 && "border-b",
                    rowTint(meta),
                  )}
                  style={{ gridTemplateColumns, top: vItem.start, height: ROW_HEIGHT }}
                >
                  <span className="flex items-center justify-center">
                    <input
                      type="checkbox"
                      className="size-4 accent-primary disabled:opacity-40"
                      checked={selected?.has(meta.index) ?? false}
                      disabled={!meta.valid}
                      onChange={() => onToggle?.(meta.index)}
                    />
                  </span>
                  {cells(meta)}
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            {rows.slice(0, maxRows ?? rows.length).map((meta) => (
              <div
                key={meta.index}
                className={cn(GRID_BASE, "border-b text-sm last:border-b-0", rowTint(meta))}
                style={{ gridTemplateColumns, height: ROW_HEIGHT }}
              >
                {cells(meta)}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
